import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Avatar as NextUIAvatar } from '@nextui-org/react';
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa'; // Import star icons

const backUrl = process.env.REACT_APP_BACK_URL;


// Styled-components for consistent layout and modern feel with a light theme
const CarouselWrapper = styled.div`
  position: relative;
  width: 95%;
  max-width: 1100px;
  overflow: hidden;
  margin: auto;
  margin-top: 20px;
  border: 1px solid #d7e6f8;
  border-radius: 22px;
  background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  transition: transform 0.35s ease-in-out;
  gap: 20px;
  padding: 20px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 10px;
    gap: 10px;
    transform: translateX(${({ currentIndex }) => -currentIndex * 100}%);
  }
`;

const CardContainer = styled.div`
  flex: 0 0 340px;
  max-width: 340px;
  min-width: 340px;
  border-radius: 14px;
  border: 1px solid #e2ecf8;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  background-color: #ffffff;
  color: #333;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
  }

  @media (max-width: 768px) {
    flex: 0 0 100%;
    max-width: 75%;
    
  }
`;



const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  min-height: 60px;
`;



const ProfileInfo = styled.div`
  display: flex;
  flex: 1;
  gap: 10px;
  align-items: flex-start;
`;

const Avatar = styled(NextUIAvatar)`
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  background-color: #fff; /* ✅ Force white background behind PNGs */
  border-radius: 50%;
  overflow: hidden;
  object-fit: cover;
`;


const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FollowButton = styled.button`
  background-color: #1f6fd1;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    background-color: #165bb0;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px 12px;
  }
`;


const CompanyName = styled.h4`
  margin: 0;
  font-size: 17px;
  color: #102a43;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px; /* prevent overflow beside avatar */

  @media (max-width: 768px) {
    max-width: 140px;
    font-size: 16px;
  }
`;


const Profession = styled.h5`
  margin: 0;
  font-size: 13px;
  color: #5b7086;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;

  @media (max-width: 768px) {
    font-size: 12px;
    max-width: 200px;
  }
`;


const CardContent = styled.div`
  margin: 12px 0;
  font-size: 14px;
  color: #475b70;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center; /* Ensures vertical alignment */
`;

const Stats = styled.div`
  display: flex;
  flex-direction: column; /* Stack items vertically */
  align-items: center; /* Center items horizontally */
  gap: 5px; /* Add spacing between label and stars */
`;



const StatValue = styled.span`
  font-weight: bold;
  color: #102a43;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: #999;
  text-align: center; /* Center the label */
`;


const StarsContainer = styled.div`
  display: flex; /* Ensure stars are aligned horizontally */
  gap: 4px; /* Spacing between stars */
`;

const AreaDetail = styled.div`
  font-size: 13px;
  color: #555;
  margin-top: 2px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;


// Fetch and Display Tradesman Data
function TradesmanCarousel() {
  const [tradesmen, setTradesmen] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  const navigate = useNavigate();

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating); // Full stars
    const halfStars = rating % 1 >= 0.5 ? 1 : 0; // Half star if rating has a significant fractional part
    const emptyStars = 5 - fullStars - halfStars; // Remaining empty stars
  
    return (
      <StarsContainer>
      {[...Array(fullStars)].map((_, index) => (
        <FaStar key={`full-${index}`} style={{ color: '#ffc107' }} />
      ))}
      {halfStars > 0 && <FaStarHalfAlt style={{ color: '#ffc107' }} />}
      {[...Array(emptyStars)].map((_, index) => (
        <FaRegStar key={`empty-${index}`} style={{ color: '#ccc' }} />
      ))}
    </StarsContainer>
    );
  };

  

  useEffect(() => {
    const fetchTradesmen = async () => {
      try {
        const response = await axios.get(`${backUrl}/tradesman/`);
        const tradesmenData = response.data;
  
        // Fetch boroughs for each using Postcodes.io
        const enhancedTradesmen = await Promise.all(
          tradesmenData.map(async (t) => {
            if (t.postcode) {
              try {
                const outcode = t.postcode.split(' ')[0];
                const outcodeRes = await axios.get(`https://api.postcodes.io/outcodes/${outcode}`);
                const borough = outcodeRes.data?.result?.admin_district?.[0];
                return { ...t, borough, outcode };
              } catch {
                return { ...t, borough: null, outcode: null };
              }
            }
            return { ...t, borough: null, outcode: null };
          })
        );
  
        setTradesmen([...enhancedTradesmen, ...enhancedTradesmen.slice(0, 3)]);
      } catch (error) {
        console.error('Error fetching tradesmen:', error.message);
      }
    };
  
    fetchTradesmen();
  }, []);
  

  // Auto-scroll every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 3000);

    return () => clearInterval(interval);
  }, [currentIndex]);

  const nextSlide = () => {
    if (carouselRef.current) {
      const totalItems = tradesmen.length;
      const maxIndex = totalItems - 3; // Subtract 3 to avoid empty space

      setCurrentIndex((prevIndex) => {
        if (prevIndex === maxIndex) {
          setTimeout(() => {
            carouselRef.current.style.transition = 'none';
            setCurrentIndex(0);
          }, 300); // Wait for the transition to finish
          return maxIndex;
        } else {
          carouselRef.current.style.transition = 'transform 0.5s ease';
          return prevIndex + 1;
        }
      });
    }
  };

  

  const handleCardClick = (tradesmanId) => {
    navigate(`/tradesman/${tradesmanId}`);
  };

  

  if (!tradesmen.length) {
    return <p>Loading...</p>;
  }

  

  return (
    <CarouselWrapper>
      <CarouselContainer ref={carouselRef} style={{ transform: `translateX(-${currentIndex * 360}px)` }}>
        {tradesmen.map((tradesman, index) => (
          <CardContainer key={index} onClick={() => handleCardClick(tradesman._id)}>
            <CardHeader>
              <ProfileInfo>
              <Avatar
  src={
    tradesman.profilePicture?.startsWith('http')
      ? tradesman.profilePicture
      : tradesman.profilePicture
        ? `${backUrl}${tradesman.profilePicture.startsWith('/') ? '' : '/'}${tradesman.profilePicture}`
        : '/TUT.png'
  }
  alt="Profile"
  onError={(e) => {
    e.target.onerror = null;
    e.target.src = '/TUT.png';
  }}
/>







                <div>
                <InfoBlock>
      <CompanyName>{tradesman.companyName}</CompanyName>
      <Profession>{tradesman.profession}</Profession>
{tradesman.borough && tradesman.outcode && (
  <AreaDetail>{`${tradesman.borough}, ${tradesman.outcode}`}</AreaDetail>
)}
    </InfoBlock>
                </div>
              </ProfileInfo>
              <FollowButton onClick={() => navigate(`/tradesman/${tradesman._id}`)}>
  View
</FollowButton>

            </CardHeader>

            <CardContent>
              {tradesman.profileInfo || 'Trusted local tradesperson.'}
            </CardContent>

            <CardFooter>
              <Stats>
                <StatValue>{tradesman.reviews.length}</StatValue>
                <StatLabel>Reviews</StatLabel>
              </Stats>
              <Stats>
              <div>
      {renderStars(tradesman.averageRating)} {/* Render stars */}
    </div>
                <StatLabel>Rating</StatLabel>
              </Stats>
            </CardFooter>
          </CardContainer>
        ))}
      </CarouselContainer>
    </CarouselWrapper>
  );
}

export default TradesmanCarousel;
